import { useCallback, useEffect, useMemo } from 'react';
import { LineSeries, AreaSeries as VisxAreaSeries } from '@visx/xychart';

import { useChartContext } from '../../../components/ChartContext';
import type { ChartDataPoint } from '../../../types/ChartDataPoint';
import { isEffectivelyInfinite, remapYValueIfRight } from '../../../utils/scaleHelpers';
import type { AreaSeriesProps } from '../../types/AreaSeriesProps';

/**
 * AreaSeries component for composable LineChart.
 *
 * Renders a filled area between two data bounds (e.g., confidence intervals).
 * Registers both the lower and upper bounds with the tooltip system for hover display.
 *
 * @example
 * ```tsx
 * <LineChart data={data}>
 *   <AreaSeries
 *     seriesKey="confidence"
 *     lowerBoundDataKey="lower"
 *     upperBoundDataKey="upper"
 *     lowerBoundDataName="Lower Bound"
 *     upperBoundDataName="Upper Bound"
 *     fillOpacity={0.2}
 *   />
 * </LineChart>
 * ```
 */
export function AreaSeries({
  seriesKey,
  lowerBoundDataKey,
  upperBoundDataKey,
  lowerBoundDataName,
  upperBoundDataName,
  color,
  fillOpacity = 0.2,
  tooltipRowIcon,
  showInTooltip = true,
  excludeFromYScale = false,
  yAxis = 'left',
}: AreaSeriesProps) {
  const {
    visibleData,
    xDataKey,
    colorScale,
    registerSeriesDisplayMetadata,
    unregisterSeriesDisplayMetadata,
    chartClipPathId,
    yScaleConfig,
    yScaleRightConfig,
  } = useChartContext();

  // When bounds are Infinity/-Infinity, substitute with that axis's domain edges so the area fills the chart
  const axisScale = yAxis === 'right' ? yScaleRightConfig : yScaleConfig;
  const domain = axisScale?.domain;
  const domainMin = domain?.[0] ?? 0;
  const domainMax = domain?.[1] ?? 1;

  // Determine color (use provided or auto-assign from ordinal scale)
  const seriesColor = color || colorScale(seriesKey);

  // Generate unique keys for tooltip registration
  const lowerSeriesKey = `${seriesKey}-lower`;
  const upperSeriesKey = `${seriesKey}-upper`;

  // Register both lower and upper series metadata for tooltip display
  // Only register if showInTooltip is true (default behavior)
  useEffect(() => {
    if (!showInTooltip) {
      return;
    }

    registerSeriesDisplayMetadata(lowerSeriesKey, {
      name: lowerBoundDataName ?? lowerBoundDataKey,
      color: seriesColor,
      dataKey: lowerBoundDataKey,
      tooltipRowIcon,
      yAxis,
    });

    registerSeriesDisplayMetadata(upperSeriesKey, {
      name: upperBoundDataName ?? upperBoundDataKey,
      color: seriesColor,
      dataKey: upperBoundDataKey,
      tooltipRowIcon,
      yAxis,
    });

    return () => {
      unregisterSeriesDisplayMetadata(lowerSeriesKey);
      unregisterSeriesDisplayMetadata(upperSeriesKey);
    };
  }, [
    showInTooltip,
    lowerSeriesKey,
    upperSeriesKey,
    lowerBoundDataName,
    upperBoundDataName,
    lowerBoundDataKey,
    upperBoundDataKey,
    seriesColor,
    tooltipRowIcon,
    yAxis,
    registerSeriesDisplayMetadata,
    unregisterSeriesDisplayMetadata,
  ]);

  // Build accessors for x, lower bound (y0), and upper bound (y)
  const xAccessor = useCallback(
    (d: ChartDataPoint | undefined) => {
      if (!d) {
        return 0;
      }
      return d[xDataKey] ?? 0;
    },
    [xDataKey],
  );

  // lowerBoundDataAccessor returns the lower bound (bottom of the area).
  // Infinity/-Infinity/effectively-infinite → domain edges so the area fills the chart; tooltip still reads raw datum.
  const lowerBoundDataAccessor = useCallback(
    (d: ChartDataPoint | undefined) => {
      if (!d) {
        return 0;
      }
      const v = (d[lowerBoundDataKey] as number) ?? 0;
      const resolved = v === -Infinity || (typeof v === 'number' && isEffectivelyInfinite(v) && v < 0) ? domainMin : v;
      return remapYValueIfRight(resolved, yAxis, yScaleRightConfig?.domain, yScaleConfig?.domain);
    },
    [lowerBoundDataKey, domainMin, yAxis, yScaleRightConfig?.domain, yScaleConfig?.domain],
  );

  // upperBoundDataAccessor returns the upper bound (top of the area).
  // Infinity/-Infinity/effectively-infinite → domain edges so the area fills the chart; tooltip still reads raw datum.
  const upperBoundDataAccessor = useCallback(
    (d: ChartDataPoint | undefined) => {
      if (!d) {
        return 0;
      }
      const v = (d[upperBoundDataKey] as number) ?? 0;
      const resolved = v === Infinity || (typeof v === 'number' && isEffectivelyInfinite(v) && v > 0) ? domainMax : v;
      return remapYValueIfRight(resolved, yAxis, yScaleRightConfig?.domain, yScaleConfig?.domain);
    },
    [upperBoundDataKey, domainMax, yAxis, yScaleRightConfig?.domain, yScaleConfig?.domain],
  );

  // Filter visibleData to only include rows where all required values are present
  // visibleData is already zoom-filtered by the provider
  const filteredData = useMemo(() => {
    return visibleData.filter((datum) => {
      const lowerValue = datum[lowerBoundDataKey];
      const upperValue = datum[upperBoundDataKey];
      return lowerValue !== null && lowerValue !== undefined && upperValue !== null && upperValue !== undefined;
    });
  }, [visibleData, lowerBoundDataKey, upperBoundDataKey]);

  return (
    <>
      <VisxAreaSeries
        dataKey={seriesKey}
        data={filteredData}
        xAccessor={xAccessor}
        yAccessor={upperBoundDataAccessor}
        y0Accessor={lowerBoundDataAccessor}
        fill={seriesColor}
        fillOpacity={fillOpacity}
        renderLine={false}
        clipPath={`url(#${chartClipPathId})`}
      />

      {/* visx handles scaling the y axis to keep all data points in view. When it does
          this, it only considers the upper bound of an area series, which means the data
          can get cut off. Here we draw an extra, invisible line series to ensure that the
          lower bounds are considered in that calculation. When excludeFromYScale is true,
          we skip this so the bounds don't affect the Y scale. */}
      {!excludeFromYScale && (
        <LineSeries
          dataKey={`${seriesKey}-lower-bound`}
          data={filteredData}
          xAccessor={xAccessor}
          yAccessor={lowerBoundDataAccessor}
          stroke="transparent"
          strokeWidth={0}
        />
      )}
    </>
  );
}

// displayName for identifying AreaSeries in children extraction (supports React.memo)
AreaSeries.displayName = 'AreaSeries';
